import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import CloseIcon from "@mui/icons-material/Close";
import { supabase } from "../../utils/supabaseClient";
import { useTheme } from "@mui/material/styles";

const LoginModal = ({ open, onClose, switchToSignUp }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [suggestGoogle, setSuggestGoogle] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    if (!open) {
      setError("");
      setMessage("");
      setEmail("");
      setPassword("");
      setLoading(false);
      setIsResettingPassword(false);
      setSuggestGoogle(false);
    }
  }, [open]);

  // --- GOOGLE LOGIN ---
  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          // Preserve current URL (including search query params) so the user
          // lands back on their results page after Google auth instead of home.
          redirectTo: window.location.href,
        },
      });
      if (error) throw error;
    } catch (error) {
      console.error("Google Login Error:", error.message);
      setError(error.message);
      setLoading(false);
    }
  };

  // --- EMAIL LOGIN ---
  const handleLogin = async () => {
    setError("");
    setMessage("");
    setLoading(true);

    // 1. Attempt Sign In
    const { data: signInData, error: signInError } =
      await supabase.auth.signInWithPassword({
        email,
        password,
      });

    // 2. Handle Errors
    if (signInError) {
      if (signInError.message.includes("Email not confirmed")) {
        setError("Email not confirmed. Please check your inbox.");
      } else {
        // Could be wrong password OR an OAuth-only account with no password set
        setError("Incorrect email or password.");
        setSuggestGoogle(true);
      }
      setLoading(false);
      return;
    }

    // 3. Success!
    // We stop here. We do NOT check payment status.
    // We just close the modal. Layout.js handles the rest.
    if (signInData?.user) {
      setLoading(false);
      if (onClose) onClose();
    }
  };

  const handleResendVerification = async () => {
    setMessage("");
    setError("");
    setLoading(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: email,
    });
    if (error) setError(`Error: ${error.message}`);
    else setMessage("Verification email resent.");
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError("Please enter your email address first.");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) setError(error.message);
    else setMessage("Password reset email sent.");
    setLoading(false);
  };

  const toggleResetPassword = () => {
    setError("");
    setMessage("");
    setIsResettingPassword(!isResettingPassword);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          width: "380px",
          minHeight: "500px",
          backgroundColor: theme.palette.background.paper,
          borderRadius: "10px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          position: "relative",
          textAlign: "center",
          color: theme.palette.text.primary,
          outline: "none",
        }}
      >
        <IconButton
          onClick={() => !loading && onClose && onClose()}
          sx={{
            position: "absolute",
            top: "10px",
            right: "10px",
            color: theme.palette.text.primary,
            "&:hover": { color: theme.palette.action.hover },
          }}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>

        {!isResettingPassword ? (
          <>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
              Log In
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
              Don't have an account? <br />
              <Typography
                component="span"
                onClick={!loading ? switchToSignUp : undefined}
                sx={{
                  color: "primary.main",
                  fontWeight: "bold",
                  cursor: loading ? "default" : "pointer",
                  textDecoration: "underline",
                  "&:hover": { color: "primary.dark" },
                }}
              >
                Sign Up to FlyBnB Pro ✈️
              </Typography>
            </Typography>

            <Button
              variant="contained"
              startIcon={<GoogleIcon />}
              disabled={loading}
              onClick={handleGoogleLogin}
              sx={{
                backgroundColor: "#4285F4",
                color: "white",
                textTransform: "none",
                fontSize: "16px",
                width: "100%",
                mb: "12px",
                "&:hover": { backgroundColor: "#357ae8" },
                opacity: loading ? 0.7 : 1,
              }}
            >
              Continue with Google
            </Button>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                my: 2,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              />
              <Typography variant="body2" sx={{ mx: 2, fontWeight: "bold" }}>
                or
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              />
            </Box>

            <TextField
              fullWidth
              label="E-mail"
              variant="outlined"
              sx={{ mb: "12px" }}
              value={email}
              onChange={(e) => { setEmail(e.target.value); setSuggestGoogle(false); setError(""); }}
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              variant="outlined"
              sx={{ mb: "12px" }}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setSuggestGoogle(false); setError(""); }}
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !loading) handleLogin();
              }}
            />

            {error && (
              <Box sx={{ mt: 1, mb: 1, textAlign: "center" }}>
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
                {error.includes("Email not confirmed") && (
                  <Button
                    onClick={handleResendVerification}
                    sx={{ display: "block", mx: "auto", mt: 1 }}
                  >
                    Resend Email
                  </Button>
                )}
                {suggestGoogle && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: "8px",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1a2a3a" : "#e8f0fe",
                      border: "1px solid #4285F4",
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                      Did you sign up with Google?
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<GoogleIcon />}
                      onClick={handleGoogleLogin}
                      size="small"
                      sx={{
                        backgroundColor: "#4285F4",
                        color: "white",
                        textTransform: "none",
                        "&:hover": { backgroundColor: "#357ae8" },
                      }}
                    >
                      Continue with Google
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {message && (
              <Typography color="success.main" sx={{ mt: 1, mb: 1 }}>
                {message}
              </Typography>
            )}

            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            <Button
              variant="contained"
              fullWidth
              onClick={handleLogin}
              disabled={loading}
              sx={{
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                fontWeight: "bold",
                fontSize: "16px",
                padding: "12px",
                width: "100%",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "primary.dark" },
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? "Checking..." : "Log in"}
            </Button>

            <Typography
              variant="body2"
              onClick={!loading ? toggleResetPassword : undefined}
              sx={{
                mt: "12px",
                color: "text.secondary",
                cursor: loading ? "default" : "pointer",
                textDecoration: "underline",
                fontSize: "0.9rem",
                "&:hover": { color: "text.primary" },
              }}
            >
              Forgot your password?
            </Typography>
            <Typography
              variant="body2"
              onClick={!loading ? switchToSignUp : undefined}
              sx={{
                mt: "6px",
                color: "text.secondary",
                cursor: loading ? "default" : "pointer",
                textDecoration: "underline",
                fontSize: "0.9rem",
                "&:hover": { color: "text.primary" },
              }}
            >
              Need an account? Sign up
            </Typography>
          </>
        ) : (
          // Reset Password View
          <>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
              Reset Password
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
              Please enter the email address associated with your account.
            </Typography>
            <TextField
              fullWidth
              label="Email"
              variant="outlined"
              sx={{ mb: 2 }}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
            {error && (
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                {error}
              </Typography>
            )}
            {message && (
              <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                {message}
              </Typography>
            )}

            {loading && <CircularProgress size={24} sx={{ mb: 2 }} />}

            <Button
              variant="contained"
              fullWidth
              onClick={handleForgotPassword}
              disabled={loading}
              sx={{
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                fontWeight: "bold",
                padding: "12px",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "primary.dark" },
                mb: 2,
              }}
            >
              {loading ? "Sending..." : "Send Reset Email"}
            </Button>
            <Typography
              variant="body2"
              onClick={toggleResetPassword}
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                color: "text.secondary",
                "&:hover": { color: "text.primary" },
              }}
            >
              Back to Login
            </Typography>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default LoginModal;
